import React, { useState, useEffect } from 'react';
import { AlertTriangle, CheckCircle, XCircle, Search } from 'lucide-react';
import { acknowledgementService } from '../../services/apiService';
import Select from '../../components/ui/Select';
import {
  supplierTermsOfPayment,
  supplierPaymentMethods,
  supplierDeliveryTerms,
  supplierModeOfDelivery,
} from '../../utils/questionnaireOptions';
import { getQuestionnaireLabel } from '../../utils/questionnaireOptions';
import { useToast } from '../../components/ui/Toast';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { useConfirm } from '../../hooks/useConfirm';

const ConflictingVendors = () => {
  const { toast } = useToast();
  const { confirmState, confirm, setConfirmState } = useConfirm();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({});
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedValues, setSelectedValues] = useState({});

  // Initialize selected values when vendor is selected
  useEffect(() => {
    if (selectedVendor) {
      const vendor = vendors.find(v => v.id === selectedVendor);
      if (vendor) {
        const vendorData = vendor.vendor_response || {};
        setSelectedValues(vendorData);
      }
    } else {
      setSelectedValues({});
    }
  }, [selectedVendor, vendors]);

  useEffect(() => {
    fetchConflictedVendors();
  }, [page, search]);

  const fetchConflictedVendors = async () => {
    try {
      setLoading(true);
      const response = await acknowledgementService.getConflicted(page, 25, search);
      
      // Safely handle response and normalize data structure for old data
      const acknowledgements = (response.data || []).map(ack => ({
        ...ack,
        questionnaire_data: (ack.questionnaire_data && typeof ack.questionnaire_data === 'object')
          ? ack.questionnaire_data
          : {},
        vendor_response: (ack.vendor_response && typeof ack.vendor_response === 'object')
          ? ack.vendor_response
          : {},
      }));
      
      setVendors(acknowledgements);
      setMeta(response.meta || {});
    } catch (err) {
      console.error('Failed to fetch conflicted vendors:', err);
      setVendors([]);
      setMeta({});
      // Don't show toast on initial load if backend isn't ready
      if (err.response?.status !== 404 && err.response?.status !== 500 && err.response?.status !== 304) {
        toast.error('Failed to fetch conflicted vendors: ' + (err.response?.data?.message || err.message));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async (acknowledgementId) => {
    // Validate that all required fields are selected
    const requiredFields = [
      'supplier_term_of_payment',
      'supplier_payment_method',
      'supplier_delivery_terms',
      'supplier_mode_of_delivery'
    ];
    
    const missingFields = requiredFields.filter(field => !selectedValues[field]);
    
    if (missingFields.length > 0) {
      toast.warning(`Please select values for all fields. Missing: ${missingFields.map(f => f.replace(/_/g, ' ')).join(', ')}`);
      return;
    }

    const confirmed = await confirm({
      title: 'Approve Changes',
      message: 'Approve selected values and vendor will get mail for his approval',
      variant: 'warning'
    });

    if (!confirmed) return;

    try {
      await acknowledgementService.resolve(acknowledgementId, { final_values: selectedValues });
      toast.success('Conflict resolved. Vendor advanced to the next approval stage.');
      fetchConflictedVendors();
      setSelectedVendor(null);
      setSelectedValues({});
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to resolve conflict');
    }
  };

  const handleReject = async (acknowledgementId) => {
    const confirmed = await confirm({
      title: 'Reject Vendor',
      message: 'Reject this vendor? This will trigger the rejection workflow.',
      variant: 'danger'
    });

    if (!confirmed) return;

    try {
      await acknowledgementService.reject(acknowledgementId);
      toast.success('Vendor rejected.');
      setTimeout(() => {
        window.location.reload();
      }, 400);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to reject vendor');
    }
  };

  // Map field keys to their corresponding option arrays
  const fieldOptionsMap = {
    supplier_term_of_payment: supplierTermsOfPayment,
    supplier_payment_method: supplierPaymentMethods,
    supplier_delivery_terms: supplierDeliveryTerms,
    supplier_mode_of_delivery: supplierModeOfDelivery,
  };

  const fields = [
    { key: 'supplier_term_of_payment', label: 'Term of Payment' },
    { key: 'supplier_payment_method', label: 'Payment Method' },
    { key: 'supplier_delivery_terms', label: 'Delivery Terms' },
    { key: 'supplier_mode_of_delivery', label: 'Mode of Delivery' },
  ];

  const renderEditableComparison = (vendor) => {
    // Safely handle old data structure - ensure questionnaire_data and vendor_response are objects
    const adminData = (vendor.questionnaire_data && typeof vendor.questionnaire_data === 'object') 
      ? vendor.questionnaire_data 
      : {};
    const vendorData = (vendor.vendor_response && typeof vendor.vendor_response === 'object')
      ? vendor.vendor_response
      : {};

    const getAgreementStatus = (fieldKey) => {
      const adminValue = adminData[fieldKey];
      const vendorValue = vendorData[fieldKey];
      const selectedValue = selectedValues[fieldKey];
      const isAgreed = selectedValue === vendorValue;
      const hasChange = adminValue !== vendorValue;
      
      return { adminValue, vendorValue, selectedValue, isAgreed, hasChange };
    };

    return (
      <div className="space-y-4">
        {fields.map((field) => {
          const status = getAgreementStatus(field.key);
          
          return (
            <div
              key={field.key}
              className={`border rounded-lg p-4 ${status.hasChange ? 'bg-amber-50 border-amber-300' : 'bg-gray-50 border-gray-200'}`}
            >
              <h4 className="font-medium text-gray-900 mb-3">{field.label}</h4>
              
              {/* Admin's original value - read-only reference */}
              <div className="bg-blue-50 p-2 rounded mb-2">
                <p className="text-xs text-gray-500 mb-1">Admin's Original Value:</p>
                <p className="text-sm text-blue-700 font-medium">
                  {getQuestionnaireLabel(field.key, status.adminValue) || 'Not specified'}
                </p>
              </div>
              
              {/* Vendor's proposed value - read-only reference */}
              <div className="bg-amber-50 p-2 rounded mb-2">
                <p className="text-xs text-gray-500 mb-1">Vendor's Proposed Value:</p>
                <p className="text-sm text-amber-700 font-medium">
                  {getQuestionnaireLabel(field.key, status.vendorValue) || 'Not specified'}
                </p>
              </div>
              
              {/* Admin's final selection - EDITABLE dropdown */}
              <div className="mt-3">
                <Select
                  label="Final Approved Value"
                  options={fieldOptionsMap[field.key] || []}
                  value={selectedValues[field.key] || ''}
                  onChange={(value) => setSelectedValues(prev => ({
                    ...prev,
                    [field.key]: value
                  }))}
                  placeholder={`Select ${field.label}`}
                  searchable
                  clearable
                  required
                />
                
                {/* Agreement indicator */}
                {selectedValues[field.key] && (
                  <div className="mt-2">
                    {status.isAgreed ? (
                      <span className="text-xs text-green-600 flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        Agreement with vendor
                      </span>
                    ) : (
                      <span className="text-xs text-amber-600 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        Admin override
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center mb-2">
          <AlertTriangle className="w-8 h-8 text-amber-500 mr-3" />
          <h1 className="text-3xl font-bold text-gray-900">Conflicting Vendors</h1>
        </div>
        <p className="text-gray-600">
          Review and resolve vendor-proposed changes to first-approval questionnaires
        </p>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search by company name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Vendor List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      ) : vendors.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <p className="text-gray-600">No conflicting vendors at this time</p>
        </div>
      ) : (
        <div className="space-y-4">
          {vendors.map((vendor) => (
            <div key={vendor.id} className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{vendor.vendor.company_name}</h3>
                  <p className="text-sm text-gray-600">{vendor.vendor.email}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Submitted: {new Date(vendor.used_at).toLocaleDateString()}
                  </p>
                </div>
                <span className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-sm font-medium">
                  Conflicted
                </span>
              </div>

              {selectedVendor === vendor.id ? (
                <>
                  {renderEditableComparison(vendor)}
                  <div className="flex gap-4 mt-6">
                    <button
                      onClick={() => {
                        setSelectedVendor(null);
                        setSelectedValues({});
                      }}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleReject(vendor.id)}
                      className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                    >
                      Reject Vendor
                    </button>
                    <button
                      onClick={() => handleResolve(vendor.id)}
                      disabled={!Object.keys(selectedValues).length || 
                        !selectedValues.supplier_term_of_payment ||
                        !selectedValues.supplier_payment_method ||
                        !selectedValues.supplier_delivery_terms ||
                        !selectedValues.supplier_mode_of_delivery}
                      className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      title={
                        (!selectedValues.supplier_term_of_payment ||
                         !selectedValues.supplier_payment_method ||
                         !selectedValues.supplier_delivery_terms ||
                         !selectedValues.supplier_mode_of_delivery)
                          ? 'Please select values for all fields'
                          : 'Approve selected values'
                      }
                    >
                      Approve Changes
                    </button>
                  </div>
                </>
              ) : (
                <button
                  onClick={() => setSelectedVendor(vendor.id)}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Review Changes
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {meta.totalPages > 1 && (
        <div className="mt-6 flex justify-center gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50"
          >
            Previous
          </button>
          <span className="px-4 py-2">
            Page {page} of {meta.totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(meta.totalPages, p + 1))}
            disabled={page === meta.totalPages}
            className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmState.isOpen}
        onClose={confirmState.onCancel}
        onConfirm={confirmState.onConfirm}
        title={confirmState.title}
        message={confirmState.message}
        variant={confirmState.variant}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
      />
    </div>
  );
};

export default ConflictingVendors;
